import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

const API_BASE_URL = "https://api.itrustsecure.com";

export default function LoginPage({ onLogin }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState("login");

  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);

  const [regFullName, setRegFullName] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [regConfirm, setRegConfirm] = useState("");
  const [regAccepted, setRegAccepted] = useState(false);
  const [regError, setRegError] = useState("");
  const [regSuccess, setRegSuccess] = useState("");
  const [regLoading, setRegLoading] = useState(false);

  const styles = {
    root: { fontFamily: "'Segoe UI', Arial, sans-serif", background: "#eef2fb", color: "#0f172a", minHeight: "100vh", display: "flex", flexDirection: "column" },
    nav: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "20px 40px", borderBottom: "1px solid rgba(59,130,246,0.15)", background: "rgba(238,242,251,0.95)", backdropFilter: "blur(12px)" },
    navLogo: { fontSize: 22, fontWeight: 800, color: "#0f172a", cursor: "pointer" },
    navLogoAccent: { color: "#3b82f6" },
    navBack: { color: "#64748b", fontSize: 14, cursor: "pointer", fontWeight: 500 },
    main: { flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "40px 20px" },
    card: { background: "rgba(255,255,255,0.85)", border: "1px solid rgba(59,130,246,0.12)", borderRadius: 20, padding: "40px", width: "100%", maxWidth: 420, backdropFilter: "blur(8px)", boxShadow: "0 4px 24px rgba(59,130,246,0.1)" },
    tabs: { display: "flex", background: "rgba(59,130,246,0.06)", borderRadius: 10, padding: 4, marginBottom: 32 },
    tab: (active) => ({ flex: 1, padding: "10px", textAlign: "center", borderRadius: 8, cursor: "pointer", fontSize: 14, fontWeight: 600, background: active ? "#3b82f6" : "transparent", color: active ? "#fff" : "#64748b", border: "none", transition: "all 0.2s" }),
    title: { fontSize: 24, fontWeight: 800, marginBottom: 6, color: "#0f172a" },
    subtitle: { fontSize: 14, color: "#64748b", marginBottom: 28 },
    label: { display: "block", fontSize: 13, fontWeight: 600, color: "#475569", marginBottom: 6 },
    input: { width: "100%", padding: "12px 14px", background: "rgba(255,255,255,0.9)", border: "1px solid rgba(59,130,246,0.2)", borderRadius: 10, color: "#0f172a", fontSize: 14, marginBottom: 16, boxSizing: "border-box", outline: "none" },
    button: { width: "100%", padding: "14px", background: "#3b82f6", color: "#fff", border: "none", borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: "pointer", marginTop: 4, boxShadow: "0 4px 12px rgba(59,130,246,0.3)" },
    buttonDisabled: { width: "100%", padding: "14px", background: "rgba(59,130,246,0.3)", color: "rgba(255,255,255,0.6)", border: "none", borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: "not-allowed", marginTop: 4 },
    error: { background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)", color: "#dc2626", padding: "10px 14px", borderRadius: 8, fontSize: 13, marginBottom: 16 },
    success: { background: "rgba(22,163,74,0.08)", border: "1px solid rgba(22,163,74,0.25)", color: "#16a34a", padding: "10px 14px", borderRadius: 8, fontSize: 13, marginBottom: 16 },
    termsRow: { display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 18, marginTop: 4 },
    termsText: { fontSize: 13, color: "#64748b", lineHeight: 1.5 },
    termsLink: { color: "#3b82f6", cursor: "pointer", fontWeight: 600, textDecoration: "underline" },
    switchText: { textAlign: "center", fontSize: 13, color: "#64748b", marginTop: 20 },
    switchLink: { color: "#3b82f6", cursor: "pointer", fontWeight: 600 },
  };

  async function handleLogin(e) {
    e.preventDefault();
    setLoginError("");
    setLoginLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: loginEmail, password: loginPassword })
      });
      const data = await res.json();
      if (!res.ok) { setLoginError(data.message || data.error || "Login failed"); return; }
      localStorage.setItem("itrust_token", data.token);
      localStorage.setItem("itrust_user", JSON.stringify(data.user));
      if (onLogin) onLogin(data.user);
      navigate("/home");
    } catch {
      setLoginError("Something went wrong. Please try again.");
    } finally { setLoginLoading(false); }
  }

  async function handleRegister(e) {
    e.preventDefault();
    setRegError(""); setRegSuccess("");
    if (regPassword !== regConfirm) { setRegError("Passwords do not match"); return; }
    if (regPassword.length < 8) { setRegError("Password must be at least 8 characters"); return; }
    if (!regAccepted) { setRegError("You must accept the Terms of Service and Privacy Policy to continue"); return; }
    setRegLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fullName: regFullName, email: regEmail, password: regPassword, acceptedTerms: true })
      });
      const data = await res.json();
      if (!res.ok) { setRegError(data.message || data.error || "Registration failed"); return; }
      localStorage.setItem("itrust_token", data.token);
      localStorage.setItem("itrust_user", JSON.stringify(data.user));
      if (onLogin) onLogin(data.user);
      navigate("/home");
    } catch {
      setRegError("Something went wrong. Please try again.");
    } finally { setRegLoading(false); }
  }

  return (
    <div style={styles.root}>
      <nav style={styles.nav}>
        <div style={styles.navLogo} onClick={() => navigate("/")}>
          i<span style={styles.navLogoAccent}>Trust Secure</span>
        </div>
        <span style={styles.navBack} onClick={() => navigate("/")}>← Back to home</span>
      </nav>
      <div style={styles.main}>
        <div style={styles.card}>
          <div style={styles.tabs}>
            <button style={styles.tab(tab === "login")} onClick={() => { setTab("login"); setLoginError(""); }}>Sign In</button>
            <button style={styles.tab(tab === "register")} onClick={() => { setTab("register"); setRegError(""); setRegSuccess(""); }}>Create Account</button>
          </div>

          {tab === "login" ? (
            <>
              <div style={styles.title}>Welcome back</div>
              <div style={styles.subtitle}>Sign in to your iTrust Secure account</div>
              {loginError && <div style={styles.error}>{loginError}</div>}
              <form onSubmit={handleLogin}>
                <label style={styles.label}>Email</label>
                <input style={styles.input} type="email" placeholder="you@example.com" value={loginEmail} onChange={e => setLoginEmail(e.target.value)} required />
                <label style={styles.label}>Password</label>
                <input style={styles.input} type="password" placeholder="••••••••" value={loginPassword} onChange={e => setLoginPassword(e.target.value)} required />
                <button type="submit" style={loginLoading ? styles.buttonDisabled : styles.button} disabled={loginLoading}>
                  {loginLoading ? "Signing in..." : "Sign In"}
                </button>
              </form>
              <div style={styles.switchText}>
                Don't have an account?{" "}
                <span style={styles.switchLink} onClick={() => setTab("register")}>Create one</span>
              </div>
            </>
          ) : (
            <>
              <div style={styles.title}>Create account</div>
              <div style={styles.subtitle}>Join iTrust Secure and trade safely</div>
              {regError && <div style={styles.error}>{regError}</div>}
              {regSuccess && <div style={styles.success}>{regSuccess}</div>}
              <form onSubmit={handleRegister}>
                <label style={styles.label}>Full Name</label>
                <input style={styles.input} type="text" placeholder="Samuel Johnson" value={regFullName} onChange={e => setRegFullName(e.target.value)} required />
                <label style={styles.label}>Email</label>
                <input style={styles.input} type="email" placeholder="you@example.com" value={regEmail} onChange={e => setRegEmail(e.target.value)} required />
                <label style={styles.label}>Password</label>
                <input style={styles.input} type="password" placeholder="Min. 8 characters" value={regPassword} onChange={e => setRegPassword(e.target.value)} required />
                <label style={styles.label}>Confirm Password</label>
                <input style={styles.input} type="password" placeholder="Repeat password" value={regConfirm} onChange={e => setRegConfirm(e.target.value)} required />

                <div style={styles.termsRow}>
                  <input
                    type="checkbox"
                    id="acceptTerms"
                    checked={regAccepted}
                    onChange={e => setRegAccepted(e.target.checked)}
                    style={{ width: 16, height: 16, marginTop: 2, cursor: "pointer", accentColor: "#3b82f6", flexShrink: 0 }}
                  />
                  <label htmlFor="acceptTerms" style={styles.termsText}>
                    I agree to the{" "}
                    <span style={styles.termsLink} onClick={() => window.open("/legal/terms", "_blank")}>Terms of Service</span>
                    {" "}and{" "}
                    <span style={styles.termsLink} onClick={() => window.open("/legal/privacy", "_blank")}>Privacy Policy</span>
                    . I confirm I am at least 18 years old.
                  </label>
                </div>

                <button type="submit" style={(regLoading || !regAccepted) ? styles.buttonDisabled : styles.button} disabled={regLoading || !regAccepted}>
                  {regLoading ? "Creating account..." : "Create Account"}
                </button>
              </form>
              <div style={styles.switchText}>
                Already have an account?{" "}
                <span style={styles.switchLink} onClick={() => setTab("login")}>Sign in</span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}